import { supabase } from '@/integrations/supabase/client';
import { CampaignStatus, ReferentStatus } from '@/types/states';
import { AuditEntry } from '@/types/unified';
import { logger } from '@/utils/productionLogger';

export interface AuditLogEntry {
  id?: string;
  entity_type: 'campaign' | 'referent' | 'business_rule';
  entity_id: string;
  action: string;
  old_value?: unknown;
  new_value?: unknown;
  user_id?: string;
  reason?: string;
  timestamp?: string;
  details?: Record<string, unknown>;
  rule_name?: string;
}

export class AuditService {
  /**
   * Log a state transition for campaigns or referents
   */
  async logStateTransition(
    entityType: 'campaign' | 'referent',
    entityId: string,
    fromState: CampaignStatus | ReferentStatus,
    toState: CampaignStatus | ReferentStatus,
    userId?: string,
    reason?: string
  ): Promise<void> {
    const auditEntry: AuditLogEntry = {
      entity_type: entityType,
      entity_id: entityId,
      action: 'state_transition',
      old_value: fromState,
      new_value: toState,
      user_id: userId,
      reason,
      timestamp: new Date().toISOString(),
    };

    await this.saveAuditEntry(auditEntry);
  }

  /**
   * Log business rule violations
   */
  async logBusinessRuleViolation(
    rule: string,
    entityId: string,
    details: Record<string, unknown>,
    userId?: string
  ): Promise<void> {
    const auditEntry: AuditLogEntry = {
      entity_type: 'business_rule',
      entity_id: entityId,
      action: 'rule_violation',
      rule_name: rule,
      details: this.sanitizeDetails(details),
      user_id: userId,
      timestamp: new Date().toISOString(),
    };

    await this.saveAuditEntry(auditEntry);
    
    // Alert administrators for critical violations
    if (this.isCriticalViolation(rule)) {
      await this.alertAdministrators(auditEntry);
    }
  }

  /**
   * Log general audit events
   */
  async logEvent(
    entityType: 'campaign' | 'referent' | 'business_rule',
    entityId: string,
    action: string,
    details?: Record<string, unknown>,
    userId?: string
  ): Promise<void> {
    const auditEntry: AuditLogEntry = {
      entity_type: entityType,
      entity_id: entityId,
      action,
      details: details ? this.sanitizeDetails(details) : undefined,
      user_id: userId,
      timestamp: new Date().toISOString(),
    };

    await this.saveAuditEntry(auditEntry);
  }

  /**
   * Log business actions (non-state transitions)
   */
  async logBusinessAction(
    action: string,
    entityId: string,
    details?: Record<string, unknown>,
    userId?: string
  ): Promise<void> {
    const auditEntry: AuditLogEntry = {
      entity_type: 'business_rule', // Utiliser 'business_rule' au lieu de 'business_action'
      entity_id: entityId,
      action,
      details: details ? this.sanitizeDetails(details) : undefined,
      user_id: userId,
      timestamp: new Date().toISOString(),
    };

    await this.saveAuditEntry(auditEntry);
  }

  /**
   * Retrieve audit entries for an entity
   */
  async getAuditHistory(
    entityType: string,
    entityId: string,
    limit: number = 50
  ): Promise<AuditEntry[]> {
    const { data, error } = await (supabase as any)
      .from('audit_log')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('Erreur lors de la récupération de l\'historique d\'audit', error);
      throw new Error('Impossible de récupérer l\'historique d\'audit');
    }

    return data || [];
  }

  /**
   * Save audit entry to database
   */
  private async saveAuditEntry(auditEntry: AuditLogEntry): Promise<void> {
    try {
      const { error } = await (supabase as any)
        .from('audit_log')
        .insert([auditEntry]);

      if (error) {
        logger.error('Erreur lors de la sauvegarde de l\'entrée d\'audit', error as Error);
        // Don't throw here to avoid breaking the main operation
        // Just log the error for monitoring
      }
    } catch (_error) {
      logger.error('Erreur inattendue lors de l\'audit', _error as Error);
      // Silent fail to not break main operations
    }
  }

  /**
   * Sanitize sensitive data from audit details
   */
  private sanitizeDetails(details: Record<string, unknown>): Record<string, unknown> {
    const sensitiveFields = [
      'ip_address',
      'user_agent', 
      'user-agent',
      'session_id',
      'password',
      'token',
      'secret',
      'key',
      'authorization',
      'cookie',
      'x-forwarded-for',
      'x-real-ip'
    ];

    const sanitizeObject = (obj: unknown): unknown => {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObject(item));
      }

      const sanitized: Record<string, any> = {};
      
      for (const [key, value] of Object.entries(obj)) {
        // Skip sensitive fields
        if (sensitiveFields.includes(key.toLowerCase())) {
          continue;
        }
        
        // Recursively sanitize nested objects
        if (typeof value === 'object' && value !== null) {
          sanitized[key] = sanitizeObject(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    };

    return sanitizeObject(details) as Record<string, unknown>;
  }

  /**
   * Determine if a business rule violation is critical
   */
  private isCriticalViolation(rule: string): boolean {
    const criticalRules = [
      'unauthorized_access',
      'data_integrity_violation',
      'security_policy_violation',
      'invalid_state_transition',
      'permission_escalation',
      'rls_bypass_attempt'
    ];

    return criticalRules.includes(rule);
  }

  /**
   * Alert administrators about critical violations
   */
  private async alertAdministrators(auditEntry: AuditLogEntry): Promise<void> {
    try {
      // Log critical violation for monitoring systems
      logger.error('CRITICAL BUSINESS RULE VIOLATION', new Error('Critical business rule violation'), {
        rule: auditEntry.rule_name,
        entity_id: auditEntry.entity_id,
        timestamp: auditEntry.timestamp,
        user_id: auditEntry.user_id
      });

      // In a real implementation, this would:
      // 1. Send notifications to administrators
      // 2. Create alerts in monitoring systems
      // 3. Potentially trigger automated responses
      
      // For now, we'll create a high-priority audit entry
      await this.saveAuditEntry({
        entity_type: 'business_rule',
        entity_id: '00000000-0000-0000-0000-000000000000', // UUID système dédié pour les alertes
        action: 'critical_violation_alert',
        details: {
          original_violation: auditEntry.rule_name,
          affected_entity: auditEntry.entity_id,
          severity: 'critical'
        },
        timestamp: new Date().toISOString()
      });

    } catch (_error) {
      logger.error('Erreur lors de l\'alerte aux administrateurs', _error as Error);
      // Silent fail to not break the audit process
    }
  }

  /**
   * Get audit statistics for monitoring
   */
  async getAuditStatistics(
    startDate: string,
    endDate: string
  ): Promise<{
    totalEvents: number;
    stateTransitions: number;
    ruleViolations: number;
    criticalViolations: number;
  }> {
    try {
      const { data, error } = await (supabase as any)
        .from('audit_log')
        .select('action, rule_name')
        .gte('timestamp', startDate)
        .lte('timestamp', endDate);

      if (error) {
        throw error;
      }

      const stats = {
        totalEvents: data?.length || 0,
        stateTransitions: data?.filter((entry: any) => entry.action === 'state_transition').length || 0,
        ruleViolations: data?.filter((entry: any) => entry.action === 'rule_violation').length || 0,
        criticalViolations: data?.filter((entry: any) => 
          entry.action === 'rule_violation' && 
          entry.rule_name && 
          this.isCriticalViolation(entry.rule_name)
        ).length || 0
      };

      return stats;
    } catch (_error) {
      logger.error('Erreur lors de la récupération des statistiques d\'audit', _error as Error);
      return {
        totalEvents: 0,
        stateTransitions: 0,
        ruleViolations: 0,
        criticalViolations: 0
      };
    }
  }
}

// Export singleton instance
export const auditService = new AuditService();